import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, orderBy, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase';
import { toast } from '@/lib/toast';
import { Trophy, Check, X, AlertTriangle, Crown } from 'lucide-react';

interface VIPRequest {
 id: string;
 userId: string;
 userName: string;
 userEmail: string;
 currentPoints: number;
 createdAt: any;
 userHistory: {
 totalServices: number;
 noShows: number;
 lateCancellations: number;
 };
 status: 'pending' | 'approved' | 'rejected';
}

interface CurrentVIP {
 id: string;
 displayName: string;
 email: string;
 loyaltyPoints: number;
 vipSince: any;
}

interface Props {
 selectedFranchiseId: string;
 selectedBranchId: string;
}

export const VIPApprovalPanel: React.FC<Props> = ({ selectedFranchiseId, selectedBranchId }) => {
 const [pendingRequests, setPendingRequests] = useState<VIPRequest[]>([]);
 const [currentVIPs, setCurrentVIPs] = useState<CurrentVIP[]>([]);
 const [loading, setLoading] = useState(true);
 const [processingId, setProcessingId] = useState<string | null>(null);
 const [comment, setComment] = useState('');
 const [showCommentFor, setShowCommentFor] = useState<string | null>(null);

 useEffect(() => {
 // Subscribe to pending VIP requests
 // Build query with all conditions at once
 const queryConstraints: any[] = [
 where('status', '==', 'pending'),
 orderBy('createdAt', 'desc')
 ];

 if (selectedFranchiseId !== 'all') {
 queryConstraints.push(where('franchiseId', '==', selectedFranchiseId));
 }

 if (selectedBranchId !== 'all') {
 queryConstraints.push(where('branchId', '==', selectedBranchId));
 }

 const requestsQuery = query(
 collection(db, 'vipRequests'),
 ...queryConstraints
 );

 const unsubRequests = onSnapshot(
 requestsQuery,
 (snapshot) => {
 const requestsData = snapshot.docs.map(doc => ({
 id: doc.id,
 ...doc.data()
 })) as VIPRequest[];

 setPendingRequests(requestsData);
 setLoading(false);
 },
 (error) => {
 console.error('Error loading VIP requests:', error);
 setLoading(false);
 toast.error('Error al cargar solicitudes VIP');
 }
 );

 // Load current VIPs
 loadCurrentVIPs();

 return () => unsubRequests();
 }, [selectedFranchiseId, selectedBranchId]);

 const loadCurrentVIPs = async () => {
 try {
 // Build query with all conditions at once
 const queryConstraints: any[] = [
 where('isVIP', '==', true)
 ];

 if (selectedFranchiseId !== 'all') {
 queryConstraints.push(where('franchiseId', '==', selectedFranchiseId));
 }

 if (selectedBranchId !== 'all') {
 queryConstraints.push(where('branchId', '==', selectedBranchId));
 }

 const vipsQuery = query(
 collection(db, 'users'),
 ...queryConstraints
 );

 const snapshot = await getDocs(vipsQuery);
 const vipsData = snapshot.docs.map(doc => ({
 id: doc.id,
 ...doc.data()
 })) as CurrentVIP[];

 setCurrentVIPs(vipsData);
 } catch (error) {
 console.error('Error loading VIPs:', error);
 }
 };

 const handleApprove = async (requestId: string) => {
 setProcessingId(requestId);

 try {
 const approveFn = httpsCallable(functions, 'approveVIP');
 await approveFn({
 requestId,
 comment: comment || undefined
 });

 toast.success('Solicitud VIP aprobada');
 setComment('');
 setShowCommentFor(null);
 loadCurrentVIPs(); // Refresh VIP list
 } catch (error: any) {
 console.error('Error approving VIP:', error);
 toast.error(error.message || 'Error al aprobar solicitud');
 } finally {
 setProcessingId(null);
 }
 };

 const handleReject = async (requestId: string) => {
 if (!comment.trim()) {
 toast.error('Debes proporcionar una razón para el rechazo');
 return;
 }

 setProcessingId(requestId);

 try {
 const rejectFn = httpsCallable(functions, 'rejectVIP');
 await rejectFn({
 requestId,
 reason: comment
 });

 toast.success('Solicitud VIP rechazada');
 setComment('');
 setShowCommentFor(null);
 } catch (error: any) {
 console.error('Error rejecting VIP:', error);
 toast.error(error.message || 'Error al rechazar solicitud');
 } finally {
 setProcessingId(null);
 }
 };

 const handleRevokeVIP = async (userId: string, userName: string) => {
 // TODO: Replace with modal input
 const reason = prompt(`¿Por qué deseas revocar el estatus VIP de ${userName}?`);

 if (!reason) return;

 try {
 const revokeFn = httpsCallable(functions, 'revokeVIPManually');
 await revokeFn({
 userId,
 reason
 });

 toast.success('Estatus VIP revocado');
 loadCurrentVIPs();
 } catch (error: any) {
 console.error('Error revoking VIP:', error);
 toast.error(error.message || 'Error al revocar VIP');
 }
 };

 return (
 <div className="space-y-6">
 {/* Pending Requests */}
 <div className="bg-white rounded-lg shadow-md bg-surface-2">
 <div className="border-b border-hairline p-4 bg-accent/10">
 <h3 className="text-lg font-semibold text-ink flex items-center gap-2">
 <Trophy className="w-5 h-5 text-accent" />
 Solicitudes VIP Pendientes
 {pendingRequests.length > 0 && (
 <span className="ml-2 px-2 py-1 bg-accent text-on-accent text-xs font-bold rounded-full">
 {pendingRequests.length}
 </span>
 )}
 </h3>
 </div>

 <div className="p-4">
 {loading ? (
 <div className="text-center py-8">
 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent/30 mx-auto"></div>
 </div>
 ) : pendingRequests.length === 0 ? (
 <div className="text-center py-8 text-ink-subtle">
 <Trophy className="w-12 h-12 mx-auto mb-2 opacity-50" />
 <p className="text-sm">No hay solicitudes VIP pendientes</p>
 </div>
 ) : (
 <div className="space-y-4">
 {pendingRequests.map((request) => (
 <div
 key={request.id}
 className="border-2 border-accent/30 rounded-lg p-4 bg-accent/10/50 bg-accent/10"
 >
 {/* User info */}
 <div className="flex justify-between items-start mb-4">
 <div>
 <p className="font-semibold text-ink text-lg">{request.userName}</p>
 <p className="text-sm text-ink-muted">{request.userEmail}</p>
 <p className="text-xs text-ink-subtle mt-1 text-ink-muted">
 Solicitado: {new Date(request.createdAt?.toDate()).toLocaleString('es-ES')}
 </p>
 </div>
 <div className="text-right">
 <p className="text-2xl font-bold text-accent">{request.currentPoints}</p>
 <p className="text-xs text-ink-subtle">puntos</p>
 </div>
 </div>

 {/* User history */}
 <div className="grid grid-cols-3 gap-3 mb-4">
 <div className="bg-white rounded-lg p-3 text-center bg-surface-2">
 <p className="text-sm text-ink-muted">Servicios</p>
 <p className="text-lg font-bold text-success">
 {request.userHistory.totalServices}
 </p>
 </div>
 <div className="bg-white rounded-lg p-3 text-center bg-surface-2">
 <p className="text-sm text-ink-muted">No-shows</p>
 <p className={`text-lg font-bold ${
 request.userHistory.noShows > 0 ? 'text-danger' : 'text-success'
 }`}>
 {request.userHistory.noShows}
 </p>
 </div>
 <div className="bg-white rounded-lg p-3 text-center bg-surface-2">
 <p className="text-sm text-ink-muted">Cancelaciones</p>
 <p className={`text-lg font-bold ${
 request.userHistory.lateCancellations > 0 ? 'text-warning' : 'text-success'
 }`}>
 {request.userHistory.lateCancellations}
 </p>
 </div>
 </div>

 {/* Warning if bad history */}
 {(request.userHistory.noShows > 2 || request.userHistory.lateCancellations > 5) && (
 <div className="bg-accent/10 border border-accent/30 rounded-lg p-3 mb-4 flex items-start gap-2">
 <AlertTriangle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
 <p className="text-xs text-accent">
 Este cliente tiene un historial de {request.userHistory.noShows} no-shows y {request.userHistory.lateCancellations} cancelaciones tardías.
 </p>
 </div>
 )}

 {/* Comment input */}
 {showCommentFor === request.id && (
 <div className="mb-4">
 <label className="block text-sm font-medium text-ink-muted mb-2 text-ink-muted">
 Comentario (opcional)
 </label>
 <textarea
 value={comment}
 onChange={(e) => setComment(e.target.value)}
 placeholder="Razón o comentario..."
 rows={2}
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle text-sm"
 />
 </div>
 )}

 {/* Actions */}
 <div className="flex gap-2">
 {showCommentFor !== request.id && (
 <button
 onClick={() => setShowCommentFor(request.id)}
 className="text-sm text-ink-muted hover:text-ink underline text-ink-muted"
 >
 Añadir comentario
 </button>
 )}
 <div className="flex-1"></div>
 <button
 onClick={() => handleReject(request.id)}
 disabled={processingId === request.id || (showCommentFor === request.id && !comment.trim())}
 className="py-2 px-4 border border-danger/30 text-danger bg-surface rounded-lg hover:bg-danger/20 disabled:opacity-50 flex items-center gap-2 text-sm font-medium"
 >
 <X className="w-4 h-4" />
 Rechazar
 </button>
 <button
 onClick={() => handleApprove(request.id)}
 disabled={processingId === request.id}
 className="py-2 px-4 bg-success text-white rounded-lg hover:bg-success/90 disabled:opacity-50 flex items-center gap-2 text-sm font-semibold"
 >
 <Check className="w-4 h-4" />
 Aprobar
 </button>
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 </div>

 {/* Current VIPs */}
 <div className="bg-white rounded-lg shadow-md bg-surface-2">
 <div className="border-b border-hairline p-4 bg-accent/10">
 <h3 className="text-lg font-semibold text-ink flex items-center gap-2">
 <Crown className="w-5 h-5 text-accent" />
 Clientes VIP Actuales
 {currentVIPs.length > 0 && (
 <span className="ml-2 px-2 py-1 bg-accent text-on-accent text-xs font-bold rounded-full">
 {currentVIPs.length}
 </span>
 )}
 </h3>
 </div>

 <div className="p-4">
 {currentVIPs.length === 0 ? (
 <div className="text-center py-8 text-ink-subtle">
 <Crown className="w-12 h-12 mx-auto mb-2 opacity-50" />
 <p className="text-sm">No hay clientes VIP actualmente</p>
 </div>
 ) : (
 <div className="space-y-3">
 {currentVIPs.map((vip) => (
 <div
 key={vip.id}
 className="border border-accent/30 rounded-lg p-4 bg-accent/10/30 bg-accent/10"
 >
 <div className="flex justify-between items-start">
 <div className="flex-1">
 <p className="font-semibold text-ink flex items-center gap-2">
 <Crown className="w-4 h-4 text-accent" />
 {vip.displayName}
 </p>
 <p className="text-sm text-ink-muted">{vip.email}</p>
 <p className="text-xs text-ink-subtle mt-1 text-ink-muted">
 VIP desde: {new Date(vip.vipSince?.toDate()).toLocaleDateString('es-ES')}
 </p>
 </div>
 <div className="text-right">
 <p className="text-xl font-bold text-accent">{vip.loyaltyPoints}</p>
 <p className="text-xs text-ink-subtle">puntos</p>
 </div>
 </div>

 {/* Warning if close to losing VIP */}
 {vip.loyaltyPoints < 550 && (
 <div className="mt-3 bg-accent/10 border border-accent/30 rounded-lg p-2 flex items-start gap-2">
 <AlertTriangle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
 <p className="text-xs text-accent">
 Cerca de perder VIP (umbral: 500 puntos)
 </p>
 </div>
 )}

 {/* Revoke button */}
 <div className="mt-3">
 <button
 onClick={() => handleRevokeVIP(vip.id, vip.displayName)}
 className="text-sm text-danger hover:text-danger underline"
 >
 Revocar VIP
 </button>
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 </div>
 </div>
 );
};
